import axios from 'axios';
import toast from 'react-hot-toast';
import { isDemoMode, demoRequest } from './demoMock.js';
import { reportFrontendError, AUTO_ERROR_PATH } from './errorReporter.js';
import { useAuth } from '../store/auth.js';
import { isPublicRoute } from './publicRoutes.js';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 90000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('civix_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const serverMsg = err.response?.data?.error;
    const url = err.config?.baseURL ? `${err.config.baseURL}${err.config.url || ''}` : (err.config?.url || '');
    let msg = serverMsg || err.message || 'Request failed';
    // Decorate ambiguous network errors with context so users can diagnose
    if (!err.response && (err.code === 'ERR_NETWORK' || /network error/i.test(err.message || ''))) {
      msg = `Network Error contacting ${url} — check internet connection or try a hard refresh (Cmd+Shift+R / Ctrl+Shift+R).`;
    } else if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
      msg = `Request timed out after ${(err.config?.timeout || 0) / 1000}s — try again or simplify the request.`;
    } else if (status) {
      msg = `${status}: ${serverMsg || err.message}`;
    }
    // Attach the decorated message so UI shows useful info
    err.message = msg;
    if (status === 401) {
      // Clear both the request token and the persisted/in-memory session.
      // Leaving civix-auth populated re-mounts the dialer after every reload.
      useAuth.getState().logout();
      if (!isPublicRoute(window.location.pathname)) window.location.href = '/login';
    } else if (status === 402) {
      // 402 is an expected business response (payment required / card declined),
      // not a frontend crash. Always handle it here so it does NOT fall through
      // to error boundaries / window.onerror and get auto-reported as a bug.
      if (err.response?.data?.code === 'CARD_REQUIRED') {
        // Hard-gate modal: keep existing BillingGate behavior.
        try {
          window.dispatchEvent(new CustomEvent('civix:card-required', {
            detail: { message: err.response?.data?.message || 'Add a card to continue.' },
          }));
        } catch (_) { /* ignore */ }
      } else {
        // Other 402s (card_declined, A2P campaign billing failures, etc.) —
        // toast the server message and let individual call sites still react
        // via the rejected promise below.
        const billingMsg = err.response?.data?.message
          || err.response?.data?.error
          || 'Payment required.';
        toast.error(billingMsg);
      }
    } else if (status >= 500) {
      // Deduplicate toast by msg so multiple concurrent 500/timeout failures don't flood the UI
      toast.error(msg, { id: `err-${status}-${msg.slice(0, 40)}` });
      // Phase 2 — report 5xx server errors to /api/feedback/auto-error.
      // Loop guard: skip if the failing call IS the reporter endpoint. We
      // primarily check the X-Civix-Auto-Error marker header (robust against
      // proxy URLs, baseURL changes, or path rewrites), with a URL-suffix
      // fallback so calls that bypass the reporter still don't recurse.
      const reqHeaders = err.config?.headers || {};
      const isMarked = !!(reqHeaders['X-Civix-Auto-Error'] || reqHeaders['x-civix-auto-error']);
      const reqUrl = (err.config?.url || '').toString();
      const looksLikeAutoError = /\/feedback\/auto-error(\?|$)/.test(reqUrl);
      if (!isMarked && !looksLikeAutoError) {
        try {
          reportFrontendError({
            errorMessage: `API ${status}: ${serverMsg || err.message || 'server error'}`,
            stack: `at ${err.config?.method?.toUpperCase() || 'GET'} ${reqUrl}`,
            source: 'fetch_5xx',
          });
        } catch (_) { /* never throw */ }
      }
    }
    return Promise.reject(err);
  }
);

const wrap = (method) => async (url, payload) => {
  if (isDemoMode()) {
    const isGet = method === 'get' || method === 'delete';
    return demoRequest(method.toUpperCase(), url, isGet ? null : payload, isGet ? payload : null);
  }
  if (method === 'get' || method === 'delete') {
    return api[method](url, { params: payload }).then((r) => r.data);
  }
  return api[method](url, payload).then((r) => r.data);
};

export const apiGet = wrap('get');
export const apiPost = wrap('post');
export const apiPut = wrap('put');
export const apiPatch = wrap('patch');
export const apiDel = wrap('delete');
